import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Portal from '@mui/material/Portal'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import useTheme from '@mui/material/styles/useTheme'
import { APP_BAR_Z_INDEX } from '@/components/AppBar/constants'
import AssistantNudgeCard from './AssistantNudgeCard'
import { ASSISTANT_ASK_BAR, ASSISTANT_NAME, askOptionLabel } from './copy'
import {
  ASSISTANT_ASK_BAR_SIZE,
  ASSISTANT_ASK_BAR_WIDTH,
  ASSISTANT_PANEL_ID,
} from './layout'
import type { AssistantNudge } from './nudge'
import {
  promptText,
  type AssistantPageSuggestions,
  type AssistantPrompt,
} from './pageSuggestions'
import SparkleIcon from './SparkleIcon'

// 8px under the button; never closer than 16px to the viewport's edges.
const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [0, 8] } },
  { name: 'preventOverflow', options: { padding: 16 } },
]

interface AssistantAskBarProps {
  /** Below md the bar stays an icon that opens the panel: no room for the field. */
  compact: boolean
  isPanelOpen: boolean
  /**
   * A conversation to go back to (messages or a briefing): with the panel
   * closed, a click reopens it on that at any width, not the field.
   */
  hasConversation: boolean
  /** The urgent task to offer, if any. */
  nudge: AssistantNudge | null
  /** "Try asking": the starter questions for the page the user is on. */
  suggestions: AssistantPageSuggestions
  /** A question typed in the field, or a suggestion (with its case). */
  onAsk: (text: string, source: 'typed' | 'suggestion', caseId?: string) => void
  onOpenPanel: () => void
  /** Reopens the panel on the conversation the user closed. */
  onResume: () => void
  onClosePanel: () => void
  onReview: () => void
  onDismissNudge: () => void
  /** The provider's ref, so closing the panel can focus this button. */
  buttonRef?: RefObject<HTMLButtonElement>
  /** The page the user is on: Escape tucks the card away until it changes. */
  page?: string
}

/**
 * Ask Attorney Share in the top bar. At rest an icon button with the
 * two-tone sparkle, at the weight of Messages and the bell beside it; the
 * nudge drops down from it. On wide screens a click opens it
 * into a field, with "Needs you" and "Try asking" below and the page dimmed;
 * on narrow ones it opens the docked panel. With a conversation going, a
 * click at any width reopens the panel on it instead. Props only, so the
 * gallery can show it without a session.
 */
const AssistantAskBar = ({
  compact,
  isPanelOpen,
  hasConversation,
  nudge,
  suggestions,
  onAsk,
  onOpenPanel,
  onResume,
  onClosePanel,
  onReview,
  onDismissNudge,
  buttonRef,
  page,
}: AssistantAskBarProps) => {
  const theme = useTheme()
  const reduceMotion = useMediaQuery('@media (prefers-reduced-motion)')
  const [isExpanded, setIsExpanded] = useState(false)
  const [query, setQuery] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const localButtonRef = useRef<HTMLButtonElement>(null)
  const launcherRef = buttonRef ?? localButtonRef
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownId = useId()
  const tryAskingId = useId()
  const nudgeMessageId = useId()
  const resumeHintId = useId()
  // Escape tucks the card away on this page, so it hides nothing under it.
  // Not Now is what drops the nudge; the next page or a new one shows it.
  const [tuckedAway, setTuckedAway] = useState<{
    page?: string
    nudgeId: string
  } | null>(null)
  const isTuckedAway =
    tuckedAway !== null &&
    tuckedAway.nudgeId === nudge?.id &&
    tuckedAway.page === page

  // The field never sits beside the open panel.
  const expanded = isExpanded && !compact && !isPanelOpen
  // While the field or the panel is open, the nudge shows as "Needs you" there.
  const showCard = Boolean(nudge) && !expanded && !isPanelOpen && !isTuckedAway
  const label = isPanelOpen ? `Close ${ASSISTANT_NAME}` : ASSISTANT_NAME
  // What a click opens: the panel on narrow screens or with a conversation
  // to go back to, otherwise the field.
  const opensPanel = compact || hasConversation
  const resumes = hasConversation && !isPanelOpen
  // Going back to a conversation, the tooltip says so; the name stays.
  const tooltip = resumes ? ASSISTANT_ASK_BAR.resumeTooltip : label
  // Described by the nudge while its card is down, and by the resume hint
  // whenever a click goes back to a conversation (the card hides the tooltip,
  // not what the click does).
  const describedBy =
    [showCard && nudgeMessageId, resumes && resumeHintId]
      .filter(Boolean)
      .join(' ') || undefined
  const question = query.trim()
  // The app's focus outline: navy on the white bar and dropdown.
  const optionFocus = {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  }
  // The open field: a navy edge (the contrast) with a soft mint glow (the
  // brand). The transparent outline is what forced-colors mode draws.
  // Open: a light search field. While the panel is open: the pale mint the
  // app uses for a selected item. Otherwise just the icon, like its neighbours.
  let shellBackground = 'transparent'
  if (expanded) shellBackground = 'background.paper'
  else if (isPanelOpen) shellBackground = 'secondary.light'
  const fieldFocusRing = {
    outline: '2px solid transparent',
    boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}, 0 0 0 4px ${theme.palette.secondary.light}`,
  }

  // Below md, or with the panel open, the field is gone: close it for good,
  // so turning the device back or closing the panel can't bring it (and the
  // dim) back unasked.
  useEffect(() => {
    if (!compact && !isPanelOpen) return
    setIsExpanded(false)
    setQuery('')
  }, [compact, isPanelOpen])

  // Set by Escape: focus goes back to the button once it is showing again.
  const restoreFocus = useRef(false)
  useEffect(() => {
    if (expanded || !restoreFocus.current) return
    restoreFocus.current = false
    launcherRef.current?.focus()
  }, [expanded, launcherRef])

  const collapse = (options?: { restoreFocus?: boolean }) => {
    restoreFocus.current = Boolean(options?.restoreFocus)
    setIsExpanded(false)
    setQuery('')
  }

  const ask = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed) return
    collapse()
    onAsk(trimmed, 'typed')
  }

  const pick = (prompt: AssistantPrompt) => {
    collapse()
    if (typeof prompt === 'string') onAsk(prompt, 'suggestion')
    else onAsk(prompt.text, 'suggestion', prompt.caseId)
  }

  const handleButtonClick = () => {
    if (isPanelOpen) onClosePanel()
    else if (hasConversation) onResume()
    else if (compact) onOpenPanel()
    else setIsExpanded(true)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return
    if (expanded) {
      event.stopPropagation()
      collapse({ restoreFocus: true })
    } else if (showCard && nudge) {
      event.stopPropagation()
      // Focus may be in the card, which is about to go.
      launcherRef.current?.focus()
      setTuckedAway({ page, nudgeId: nudge.id })
    }
  }

  // Tabbing out of the field and dropdown closes them. A click on something
  // that takes no focus has no related target: the dim and the click-away
  // handle that.
  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget
    if (expanded && next && !event.currentTarget.contains(next)) collapse()
  }

  const handleInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault()
      ask(query)
    } else if (event.key === 'ArrowDown') {
      // Into the dropdown, as from a search field to its suggestions.
      const first = dropdownRef.current?.querySelector('button')
      if (!first) return
      event.preventDefault()
      first.focus()
    }
  }

  // Up and down step through the dropdown's buttons; up from the first goes
  // back to the field. Held here, so the page behind never scrolls.
  const handleDropdownKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const buttons = Array.from(
      dropdownRef.current?.querySelectorAll('button') ?? []
    )
    const index = buttons.indexOf(event.target as HTMLButtonElement)
    if (index === -1) return
    event.preventDefault()
    const next = index + (event.key === 'ArrowDown' ? 1 : -1)
    if (next < 0) inputRef.current?.focus()
    else buttons[next]?.focus()
  }

  // A press on the dropdown that isn't on a control would send focus to the
  // page, where Escape and typing reach nothing: keep it where it is.
  const keepFocus = (event: MouseEvent<HTMLElement>) => {
    if ((event.target as Element).closest('button, input')) return
    event.preventDefault()
  }

  // A press anywhere on the open pill puts the cursor in the field.
  const focusField = (event: MouseEvent<HTMLElement>) => {
    if ((event.target as Element).closest('input')) return
    event.preventDefault()
    inputRef.current?.focus()
  }

  const dropdown = (
    <Paper
      ref={dropdownRef}
      id={dropdownId}
      role="group"
      aria-label={ASSISTANT_ASK_BAR.dropdownLabel}
      data-testid="assistant-ask-dropdown"
      elevation={4}
      onKeyDown={handleDropdownKeyDown}
      onMouseDown={keepFocus}
      sx={{
        width: ASSISTANT_ASK_BAR_WIDTH,
        maxWidth: 'calc(100vw - 32px)',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRadius: 2,
      }}
    >
      {question && (
        <ButtonBase
          onClick={() => ask(question)}
          sx={{
            justifyContent: 'flex-start',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            typography: 'body2',
            color: 'primary.main',
            textAlign: 'left',
            '&:hover': { bgcolor: 'primary.lightest' },
            '&.Mui-focusVisible': {
              bgcolor: 'primary.lightest',
              ...optionFocus,
            },
          }}
        >
          <SparkleIcon small sx={{ fontSize: 16, flexShrink: 0 }} />
          {askOptionLabel(question)}
        </ButtonBase>
      )}
      {nudge && (
        <AssistantNudgeCard
          variant="inline"
          nudge={nudge}
          onReview={() => {
            collapse()
            onReview()
          }}
          onDismiss={() => {
            // The card is about to go; keep focus in the field.
            inputRef.current?.focus()
            onDismissNudge()
          }}
        />
      )}
      <Box>
        <Typography
          id={tryAskingId}
          variant="overline"
          component="p"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          {ASSISTANT_ASK_BAR.tryAsking}
        </Typography>
        <Box
          component="ul"
          aria-labelledby={tryAskingId}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          {suggestions.prompts.map(prompt => (
            <li key={promptText(prompt)}>
              {/* A button styled as a link, like the app's other inline actions. */}
              <ButtonBase
                onClick={() => pick(prompt)}
                sx={{
                  typography: 'body2',
                  color: 'primary.main',
                  textAlign: 'left',
                  borderRadius: 0.5,
                  '&:hover': { textDecoration: 'underline' },
                  '&.Mui-focusVisible': optionFocus,
                }}
              >
                {promptText(prompt)}
              </ButtonBase>
            </li>
          ))}
        </Box>
      </Box>
    </Paper>
  )

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          if (expanded) collapse()
        }}
      >
        <Box
          ref={setAnchorEl}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          sx={{
            position: 'relative',
            width: ASSISTANT_ASK_BAR_SIZE,
            height: ASSISTANT_ASK_BAR_SIZE,
            flexShrink: 0,
          }}
        >
          {/* One element from circle to pill, so the width animates. It
              grows leftward, over the nav links. */}
          <Box
            onMouseDown={expanded ? focusField : undefined}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              width: expanded
                ? ASSISTANT_ASK_BAR_WIDTH
                : ASSISTANT_ASK_BAR_SIZE,
              height: ASSISTANT_ASK_BAR_SIZE,
              borderRadius: `${ASSISTANT_ASK_BAR_SIZE / 2}px`,
              bgcolor: shellBackground,
              color: 'text.primary',
              boxShadow: expanded
                ? `inset 0 0 0 1px ${theme.palette.primary.light}`
                : 'none',
              overflow: expanded ? 'hidden' : 'visible',
              transition: reduceMotion
                ? 'none'
                : theme.transitions.create(['width', 'background-color'], {
                    duration: theme.transitions.duration.standard,
                  }),
              ...(expanded && { '&:focus-within': fieldFocusRing }),
            }}
          >
            {expanded ? (
              <Box
                role="search"
                aria-label={ASSISTANT_NAME}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <SparkleIcon
                  color="primary"
                  twoTone
                  sx={{ fontSize: 22, ml: 1.25, flexShrink: 0 }}
                />
                <InputBase
                  autoFocus
                  inputRef={inputRef}
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder={ASSISTANT_ASK_BAR.placeholder}
                  inputProps={{
                    'aria-label': ASSISTANT_ASK_BAR.inputLabel,
                    'aria-controls': dropdownId,
                    autoComplete: 'off',
                    enterKeyHint: 'send',
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 1,
                    color: 'inherit',
                    typography: 'body2',
                    '& input::placeholder': {
                      color: 'text.secondary',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            ) : (
              // No tooltip while the card is down (both drop below the
              // button) or the panel is open (it would sit on the panel's
              // header); the name still says "Close".
              <Tooltip title={showCard || isPanelOpen ? '' : tooltip}>
                <ButtonBase
                  ref={launcherRef}
                  data-testid="assistant-launcher"
                  aria-label={label}
                  aria-expanded={isPanelOpen}
                  aria-controls={isPanelOpen ? ASSISTANT_PANEL_ID : undefined}
                  aria-haspopup={opensPanel ? 'dialog' : undefined}
                  aria-describedby={describedBy}
                  onClick={handleButtonClick}
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 'inherit',
                    '&:hover': {
                      bgcolor: isPanelOpen
                        ? 'secondary.light'
                        : 'primary.lightest',
                    },
                    '&.Mui-focusVisible': optionFocus,
                  }}
                >
                  {/* 26px: a filled sparkle reads smaller than the outlined icons beside it. */}
                  <SparkleIcon color="primary" twoTone sx={{ fontSize: 26 }} />
                </ButtonBase>
              </Tooltip>
            )}
          </Box>
          {/* The button's description only: hidden, so it isn't read twice. */}
          {resumes && (
            <Box component="span" id={resumeHintId} hidden>
              {ASSISTANT_ASK_BAR.resumeTooltip}
            </Box>
          )}
          {/* In place, not in a portal: Tab goes from the field into the
              dropdown, and it shares the app bar's layer above the dim. */}
          <Popper
            open={Boolean(anchorEl) && (expanded || showCard)}
            anchorEl={anchorEl}
            placement="bottom-end"
            disablePortal
            role="presentation"
            modifiers={POPPER_MODIFIERS}
          >
            {expanded
              ? dropdown
              : nudge && (
                  <AssistantNudgeCard
                    nudge={nudge}
                    messageId={nudgeMessageId}
                    onReview={onReview}
                    onDismiss={() => {
                      // The card is about to go; keep focus in the top bar.
                      launcherRef.current?.focus()
                      onDismissNudge()
                    }}
                  />
                )}
          </Popper>
        </Box>
      </ClickAwayListener>
      {expanded && (
        // Dims the page but not the app bar, which sits one level up.
        <Portal>
          <Backdrop
            open
            data-testid="assistant-ask-backdrop"
            onClick={() => collapse()}
            transitionDuration={reduceMotion ? 0 : undefined}
            sx={{ zIndex: APP_BAR_Z_INDEX - 1 }}
          />
        </Portal>
      )}
    </>
  )
}

export default AssistantAskBar
